<template>
  <v-row>
    <v-breadcrumbs :items="['reports', 'cgt disposals']" divider="/"></v-breadcrumbs>
  </v-row>

  <!-- input card -->
  <v-row>
    <v-col cols="12" md="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">cgt disposals report</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
            <v-row align="center">
              <v-col>
                <v-select
                  v-model="selectedBrokerAccounts"
                  :items="brokerAccounts"
                  item-title="label"
                  item-value="id"
                  label="broker accounts"
                  density="compact"
                  variant="outlined"
                  hide-details
                  multiple
                  chips
                  closable-chips
                ></v-select>
              </v-col>
              <v-col>
                <v-select
                  v-model="selectedFY"
                  :items="financialYears"
                  label="financial year"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  @update:modelValue="onFYSelect"
                ></v-select>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="startDate"
                  type="date"
                  label="start date"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col>
                <v-text-field
                  v-model="endDate"
                  type="date"
                  label="end date"
                  density="compact"
                  variant="outlined"
                  hide-details
                ></v-text-field>
              </v-col>
              <v-col cols="auto">
                <v-btn @click="getDisposals" :loading="loading" :disabled="selectedBrokerAccounts.length === 0 || !startDate || !endDate">go</v-btn>
                <v-btn class="ml-2" variant="outlined" @click="exportPDF" :disabled="!report" prepend-icon="mdi-file-pdf-box">pdf</v-btn>
                <v-btn class="ml-2" variant="outlined" @click="exportCSV" :disabled="!report" prepend-icon="mdi-file-delimited">csv</v-btn>
              </v-col>
            </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- warning banner for prior unallocated sells -->
  <v-row v-if="hasUnallocatedPriorSells">
    <v-col cols="12">
      <v-alert type="warning" variant="tonal" closable>
        <div class="d-flex align-center justify-space-between">
          <span>there are historical sells without CGT allocations. this may affect the accuracy of available parcels for the current period.</span>
          <v-btn class="ml-4" size="small" @click="bootstrapAllocations" :loading="bootstrapping">auto-allocate (FIFO)</v-btn>
        </div>
      </v-alert>
    </v-col>
  </v-row>

  <!-- ATO summary card -->
  <v-row v-if="report && atoSummary">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">net capital gain calculation</v-card-title>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="text-left pa-4">
            <!-- off-platform entries -->
            <div v-if="selectedFY" class="mb-4">
              <div class="d-flex align-center mb-2">
                <v-icon size="small" class="mr-1" @click="offPlatformExpanded = !offPlatformExpanded" style="cursor: pointer;">{{ offPlatformExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                <span class="text-subtitle-2" @click="offPlatformExpanded = !offPlatformExpanded" style="cursor: pointer;">off-platform gains</span>
                <v-btn size="x-small" variant="text" icon="mdi-plus" class="ml-2" @click="startOffPlatformAdd" :disabled="!selectedFY"></v-btn>
                <v-spacer></v-spacer>
                <span v-if="offPlatformEntries.length > 0" class="text-caption text-grey">
                  {{ offPlatformEntries.length }} {{ offPlatformEntries.length === 1 ? 'entry' : 'entries' }}
                </span>
              </div>
              <div v-if="offPlatformExpanded">
                <template v-for="entry in offPlatformEntries" :key="entry.id">
                  <template v-if="offPlatformEditing === entry.id">
                    <v-row dense class="mb-1 align-center">
                      <v-col cols="4"><v-text-field v-model="offPlatformForm.description" density="compact" variant="outlined" hide-details></v-text-field></v-col>
                      <v-col cols="auto">
                        <v-btn size="x-small" variant="text" icon="mdi-check" @click="saveOffPlatformEntry"></v-btn>
                        <v-btn size="x-small" variant="text" icon="mdi-close" @click="cancelOffPlatformEdit"></v-btn>
                      </v-col>
                    </v-row>
                    <v-row dense class="mb-1">
                      <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">discount gains</v-col>
                      <v-col cols="4" class="d-flex justify-end"><v-text-field v-model.number="offPlatformForm.discount_gains" type="number" density="compact" variant="outlined" hide-details prefix="$" style="max-width: 150px;"></v-text-field></v-col>
                    </v-row>
                    <v-row dense class="mb-1">
                      <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">non-discount gains</v-col>
                      <v-col cols="4" class="d-flex justify-end"><v-text-field v-model.number="offPlatformForm.non_discount_gains" type="number" density="compact" variant="outlined" hide-details prefix="$" style="max-width: 150px;"></v-text-field></v-col>
                    </v-row>
                    <v-row dense class="mb-3">
                      <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">capital losses</v-col>
                      <v-col cols="4" class="d-flex justify-end"><v-text-field v-model.number="offPlatformForm.capital_losses" type="number" density="compact" variant="outlined" hide-details prefix="$" style="max-width: 150px;"></v-text-field></v-col>
                    </v-row>
                  </template>
                  <template v-else>
                    <div class="text-subtitle-2 mb-2 d-flex align-center">
                      <span class="pl-4">{{ entry.description }}</span>
                      <v-btn size="x-small" variant="text" icon="mdi-pencil" class="ml-1" @click="startOffPlatformEdit(entry)"></v-btn>
                      <v-btn size="x-small" variant="text" icon="mdi-delete" @click="deleteOffPlatformEntry(entry.id)"></v-btn>
                    </div>
                    <v-row dense class="mb-1">
                      <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">discount gains</v-col>
                      <v-col cols="4" class="text-body-2 text-right text-green">{{ formatCurrency(parseFloat(entry.discount_gains)) }}</v-col>
                    </v-row>
                    <v-row dense class="mb-1">
                      <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">non-discount gains</v-col>
                      <v-col cols="4" class="text-body-2 text-right text-green">{{ formatCurrency(parseFloat(entry.non_discount_gains)) }}</v-col>
                    </v-row>
                    <v-row dense class="mb-3">
                      <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">capital losses</v-col>
                      <v-col cols="4" class="text-body-2 text-right text-red">{{ parseFloat(entry.capital_losses) ? '-' + formatCurrency(parseFloat(entry.capital_losses)) : formatCurrency(0) }}</v-col>
                    </v-row>
                  </template>
                </template>
                <!-- new entry form -->
                <template v-if="offPlatformEditing === 'new'">
                  <v-row dense class="mb-1 align-center">
                    <v-col cols="4"><v-text-field v-model="offPlatformForm.description" density="compact" variant="outlined" hide-details placeholder="e.g. options - CommSec"></v-text-field></v-col>
                    <v-col cols="auto">
                      <v-btn size="x-small" variant="text" icon="mdi-check" @click="saveOffPlatformEntry" :disabled="!offPlatformForm.description"></v-btn>
                      <v-btn size="x-small" variant="text" icon="mdi-close" @click="cancelOffPlatformEdit"></v-btn>
                    </v-col>
                  </v-row>
                  <v-row dense class="mb-1">
                    <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">discount gains</v-col>
                    <v-col cols="4" class="d-flex justify-end"><v-text-field v-model.number="offPlatformForm.discount_gains" type="number" density="compact" variant="outlined" hide-details prefix="$" style="max-width: 150px;"></v-text-field></v-col>
                  </v-row>
                  <v-row dense class="mb-1">
                    <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">non-discount gains</v-col>
                    <v-col cols="4" class="d-flex justify-end"><v-text-field v-model.number="offPlatformForm.non_discount_gains" type="number" density="compact" variant="outlined" hide-details prefix="$" style="max-width: 150px;"></v-text-field></v-col>
                  </v-row>
                  <v-row dense class="mb-3">
                    <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">capital losses</v-col>
                    <v-col cols="4" class="d-flex justify-end"><v-text-field v-model.number="offPlatformForm.capital_losses" type="number" density="compact" variant="outlined" hide-details prefix="$" style="max-width: 150px;"></v-text-field></v-col>
                  </v-row>
                </template>
                <div v-if="offPlatformEntries.length === 0 && offPlatformEditing !== 'new'" class="text-caption text-grey mb-2">no off-platform entries for {{ selectedFY }}</div>
              </div>
              <v-divider class="mb-3"></v-divider>
            </div>

            <!-- step 1 -->
            <div class="text-subtitle-2 mb-2 d-flex align-center"><span class="step-number">1</span>classify gains</div>
            <v-row dense class="mb-1">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">discount gains (held >12 months)</v-col>
              <v-col cols="4" class="text-body-2 text-right text-green">{{ formatCurrency(atoSummary.totalDiscountGains) }}</v-col>
            </v-row>
            <v-row dense class="mb-1">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">non-discount gains (held <=12 months)</v-col>
              <v-col cols="4" class="text-body-2 text-right text-green">{{ formatCurrency(atoSummary.totalNonDiscountGains) }}</v-col>
            </v-row>
            <v-row dense class="mb-3">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">capital losses</v-col>
              <v-col cols="4" class="text-body-2 text-right text-red">{{ formatCurrency(atoSummary.totalCapitalLosses) }}</v-col>
            </v-row>

            <v-divider class="mb-3"></v-divider>

            <!-- step 2 -->
            <div class="text-subtitle-2 mb-2 d-flex align-center"><span class="step-number">2</span>apply current year losses</div>
            <v-row dense class="mb-1">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">non-discount gains</v-col>
              <v-col cols="4" class="text-body-2 text-right">
                <div>{{ formatCurrency(atoSummary.totalNonDiscountGains) }}</div>
                <div class="text-red">- {{ formatCurrency(atoSummary.lossesToNonDiscount) }}</div>
                <div class="font-weight-bold step-result">{{ formatCurrency(atoSummary.nonDiscountAfterLosses) }}</div>
              </v-col>
            </v-row>
            <v-row dense class="mb-3">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">discount gains</v-col>
              <v-col cols="4" class="text-body-2 text-right">
                <div>{{ formatCurrency(atoSummary.totalDiscountGains) }}</div>
                <div class="text-red">- {{ formatCurrency(atoSummary.lossesToDiscount) }}</div>
                <div class="font-weight-bold step-result">{{ formatCurrency(atoSummary.discountAfterLosses) }}</div>
              </v-col>
            </v-row>

            <v-divider class="mb-3"></v-divider>

            <!-- step 3 -->
            <div class="text-subtitle-2 mb-2 d-flex align-center"><span class="step-number">3</span>apply carry-forward losses</div>
            <v-row dense class="mb-1 align-center">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">prior year losses</v-col>
              <v-col cols="4" class="d-flex align-center justify-end">
                <v-btn size="small" variant="text" class="mr-1 cf-save-btn" @click="saveCarryForwardLoss" :disabled="!carryForwardKey()">
                  <v-icon :class="{ 'cf-icon-fade': cfSaved }" :color="cfSaved ? 'green' : ''">{{ cfSaved ? 'mdi-check' : 'mdi-content-save' }}</v-icon>
                </v-btn>
                <v-text-field
                  :model-value="formatCarryForwardDisplay"
                  @update:model-value="parseCarryForwardInput"
                  @focus="cfEditing = true"
                  @blur="onCarryForwardBlur"
                  density="compact"
                  variant="outlined"
                  hide-details
                  prefix="$"
                  style="max-width: 150px;"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense class="mb-1">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">non-discount gains</v-col>
              <v-col cols="4" class="text-body-2 text-right">
                <div>{{ formatCurrency(atoSummary.nonDiscountAfterLosses) }}</div>
                <div class="text-red">- {{ formatCurrency(atoSummary.cfToNonDiscount) }}</div>
                <div class="font-weight-bold step-result">{{ formatCurrency(atoSummary.nonDiscountFinal) }}</div>
              </v-col>
            </v-row>
            <v-row dense class="mb-3">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">discount gains</v-col>
              <v-col cols="4" class="text-body-2 text-right">
                <div>{{ formatCurrency(atoSummary.discountAfterLosses) }}</div>
                <div class="text-red">- {{ formatCurrency(atoSummary.cfToDiscount) }}</div>
                <div class="font-weight-bold step-result">{{ formatCurrency(atoSummary.discountFinal) }}</div>
              </v-col>
            </v-row>

            <v-divider class="mb-3"></v-divider>

            <!-- step 4 -->
            <div class="text-subtitle-2 mb-2 d-flex align-center"><span class="step-number">4</span>apply CGT discount (50%)</div>
            <v-row dense class="mb-3">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">50% discount on remaining {{ formatCurrency(atoSummary.discountFinal) }}</v-col>
              <v-col cols="4" class="text-body-2 text-right text-red">{{ atoSummary.discountAmount > 0 ? '(' + formatCurrency(atoSummary.discountAmount) + ')' : '-' }}</v-col>
            </v-row>

            <v-divider class="mb-3"></v-divider>

            <!-- step 5 -->
            <div class="text-subtitle-2 mb-2 d-flex align-center"><span class="step-number">5</span>result</div>
            <v-row dense class="mb-1">
              <v-col cols="8" class="text-body-2 font-weight-bold pl-4">net capital gain (taxable)</v-col>
              <v-col cols="4" class="text-body-2 text-right font-weight-bold" :class="atoSummary.netCapitalGain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(atoSummary.netCapitalGain) }}</v-col>
            </v-row>
            <v-row dense v-if="atoSummary.netLossToCarry > 0">
              <v-col cols="8" class="text-body-2 text-grey-darken-1 pl-4">net capital loss to carry forward</v-col>
              <v-col cols="4" class="text-body-2 text-right text-red">{{ formatCurrency(atoSummary.netLossToCarry) }}</v-col>
            </v-row>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- disposals table -->
  <v-row v-if="report && report.disposals.length > 0">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-item>
          <v-card-title class="text-left">disposals</v-card-title>
          <template v-slot:append>
            <v-btn v-if="report.has_foreign_currency" icon="mdi-currency-usd" :variant="isBaseCurrency ? 'tonal' : 'text'" @click="isBaseCurrency = !isBaseCurrency"></v-btn>
          </template>
        </v-card-item>
        <v-divider></v-divider>
        <v-card-text class="pa-4">
            <table>
              <tr>
                <th></th>
                <th>date</th>
                <th>broker</th>
                <th>ticker</th>
                <th>ccy</th>
                <th>qty</th>
                <th>price</th>
                <th>brokerage</th>
                <th>proceeds</th>
                <th>gain</th>
                <th>type</th>
                <th>method</th>
                <th>actions</th>
              </tr>
              <template v-for="(disposal, index) in report.disposals" :key="disposal.sell_txn.id">
                <tr @click="toggleExpand(index)" style="cursor: pointer;">
                  <td><v-icon size="small">{{ expandedRows[index] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon></td>
                  <td>{{ showDateFormat(disposal.sell_txn.txn_date) }}</td>
                  <td>{{ brokerAccountLabel(disposal.sell_txn.broker_account_id) }}</td>
                  <td>{{ disposal.ticker }}</td>
                  <td>{{ isBaseCurrency && disposal.sell_txn.currency && disposal.sell_txn.currency !== 'AUD' ? 'AUD' : (disposal.sell_txn.currency || 'AUD') }}</td>
                  <td>{{ disposal.sell_txn.qty.toLocaleString() }}</td>
                  <td>{{ isBaseCurrency && disposal.sell_txn.base_currency_net_amount ? formatCurrency(disposal.sell_txn.base_currency_net_amount / disposal.sell_txn.qty) : formatCurrency(disposal.sell_txn.price) }}</td>
                  <td>{{ isBaseCurrency && disposal.sell_txn.base_currency_spot_rate ? formatCurrency(disposal.sell_txn.brokerage / disposal.sell_txn.base_currency_spot_rate) : formatCurrency(disposal.sell_txn.brokerage) }}</td>
                  <td>{{ formatCurrency(activeSource(disposal).allocations.reduce((s, a) => s + a.proceeds, 0)) }}</td>
                  <td :class="activeSource(disposal).total_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(activeSource(disposal).total_gain) }}</td>
                  <td><v-chip size="x-small" :color="disposalGainType(disposal).color" variant="tonal">{{ disposalGainType(disposal).label }}</v-chip></td>
                  <td>{{ disposal.has_persisted_allocation ? disposal.persisted_allocation.method : '-' }}</td>
                  <td>
                    <v-btn size="x-small" variant="text" icon="mdi-pencil" @click.stop="openAllocationModal(disposal)"></v-btn>
                    <v-btn v-if="disposal.has_persisted_allocation" size="x-small" variant="text" icon="mdi-delete" @click.stop="deleteAllocation(disposal.sell_txn.id)"></v-btn>
                  </td>
                </tr>
                <!-- expanded row detail -->
                <tr v-if="expandedRows[index]">
                  <td colspan="13" class="pa-0">
                    <v-card flat class="ma-2 bg-grey-lighten-5">
                      <v-card-text>
                        <div class="text-subtitle-2 mb-3 text-left">open units before disposal: {{ disposal.open_units_at_disposal.toLocaleString() }}</div>

                        <v-tabs v-model="activeTab[index]" density="compact" class="mb-3">
                          <v-tooltip text="first in first out" location="top"><template v-slot:activator="{ props }"><v-tab value="fifo" v-bind="props">FIFO</v-tab></template></v-tooltip>
                          <v-tooltip text="last in first out" location="top"><template v-slot:activator="{ props }"><v-tab value="lifo" v-bind="props">LIFO</v-tab></template></v-tooltip>
                          <v-tooltip text="lowest cost first out" location="top"><template v-slot:activator="{ props }"><v-tab value="lofo" v-bind="props">LOFO</v-tab></template></v-tooltip>
                          <v-tooltip text="highest cost first out" location="top"><template v-slot:activator="{ props }"><v-tab value="hifo" v-bind="props">HIFO</v-tab></template></v-tooltip>

                          <v-tab v-if="disposal.has_persisted_allocation" value="saved">saved ({{ disposal.persisted_allocation.method }})</v-tab>
                        </v-tabs>

                        <v-window v-model="activeTab[index]">
                          <v-window-item value="fifo">
                            <table class="detail-table mb-4">
                              <tr>
                                <th>buy date</th><th>qty</th><th>unit cost</th><th>cost base</th><th>proceeds</th><th>gain</th><th>days held</th><th>discount eligible</th>
                              </tr>
                              <tr v-for="alloc in disposal.fifo_preview.allocations" :key="'fifo-' + alloc.buy_txn_id">
                                <td>{{ showDateFormat(alloc.buy_date) }}</td>
                                <td>{{ alloc.qty.toLocaleString() }} / {{ parcelTotalQty(disposal, alloc.buy_txn_id).toLocaleString() }}</td>
                                <td>{{ formatCurrency(alloc.unit_cost) }}</td>
                                <td>{{ formatCurrency(alloc.cost_base) }}</td>
                                <td>{{ formatCurrency(alloc.proceeds) }}</td>
                                <td :class="alloc.capital_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(alloc.capital_gain) }}</td>
                                <td>{{ alloc.holding_days }}</td>
                                <td>{{ alloc.discount_eligible ? 'yes' : 'no' }}</td>
                              </tr>
                              <tr v-if="disposal.fifo_preview.allocations.length > 1" class="font-weight-bold">
                                <td></td><td></td><td></td><td></td><td></td>
                                <td :class="disposal.fifo_preview.total_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(disposal.fifo_preview.total_gain) }}</td>
                                <td></td><td></td>
                              </tr>
                            </table>
                          </v-window-item>

                          <v-window-item value="lifo">
                            <table class="detail-table mb-4">
                              <tr>
                                <th>buy date</th><th>qty</th><th>unit cost</th><th>cost base</th><th>proceeds</th><th>gain</th><th>days held</th><th>discount eligible</th>
                              </tr>
                              <tr v-for="alloc in disposal.lifo_preview.allocations" :key="'lifo-' + alloc.buy_txn_id">
                                <td>{{ showDateFormat(alloc.buy_date) }}</td>
                                <td>{{ alloc.qty.toLocaleString() }} / {{ parcelTotalQty(disposal, alloc.buy_txn_id).toLocaleString() }}</td>
                                <td>{{ formatCurrency(alloc.unit_cost) }}</td>
                                <td>{{ formatCurrency(alloc.cost_base) }}</td>
                                <td>{{ formatCurrency(alloc.proceeds) }}</td>
                                <td :class="alloc.capital_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(alloc.capital_gain) }}</td>
                                <td>{{ alloc.holding_days }}</td>
                                <td>{{ alloc.discount_eligible ? 'yes' : 'no' }}</td>
                              </tr>
                              <tr v-if="disposal.lifo_preview.allocations.length > 1" class="font-weight-bold">
                                <td></td><td></td><td></td><td></td><td></td>
                                <td :class="disposal.lifo_preview.total_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(disposal.lifo_preview.total_gain) }}</td>
                                <td></td><td></td>
                              </tr>
                            </table>
                          </v-window-item>

                          <v-window-item value="lofo">
                            <table class="detail-table mb-4">
                              <tr>
                                <th>buy date</th><th>qty</th><th>unit cost</th><th>cost base</th><th>proceeds</th><th>gain</th><th>days held</th><th>discount eligible</th>
                              </tr>
                              <tr v-for="alloc in disposal.lofo_preview.allocations" :key="'lofo-' + alloc.buy_txn_id">
                                <td>{{ showDateFormat(alloc.buy_date) }}</td>
                                <td>{{ alloc.qty.toLocaleString() }} / {{ parcelTotalQty(disposal, alloc.buy_txn_id).toLocaleString() }}</td>
                                <td>{{ formatCurrency(alloc.unit_cost) }}</td>
                                <td>{{ formatCurrency(alloc.cost_base) }}</td>
                                <td>{{ formatCurrency(alloc.proceeds) }}</td>
                                <td :class="alloc.capital_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(alloc.capital_gain) }}</td>
                                <td>{{ alloc.holding_days }}</td>
                                <td>{{ alloc.discount_eligible ? 'yes' : 'no' }}</td>
                              </tr>
                              <tr v-if="disposal.lofo_preview.allocations.length > 1" class="font-weight-bold">
                                <td></td><td></td><td></td><td></td><td></td>
                                <td :class="disposal.lofo_preview.total_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(disposal.lofo_preview.total_gain) }}</td>
                                <td></td><td></td>
                              </tr>
                            </table>
                          </v-window-item>

                          <v-window-item value="hifo">
                            <table class="detail-table mb-4">
                              <tr>
                                <th>buy date</th><th>qty</th><th>unit cost</th><th>cost base</th><th>proceeds</th><th>gain</th><th>days held</th><th>discount eligible</th>
                              </tr>
                              <tr v-for="alloc in disposal.hifo_preview.allocations" :key="'hifo-' + alloc.buy_txn_id">
                                <td>{{ showDateFormat(alloc.buy_date) }}</td>
                                <td>{{ alloc.qty.toLocaleString() }} / {{ parcelTotalQty(disposal, alloc.buy_txn_id).toLocaleString() }}</td>
                                <td>{{ formatCurrency(alloc.unit_cost) }}</td>
                                <td>{{ formatCurrency(alloc.cost_base) }}</td>
                                <td>{{ formatCurrency(alloc.proceeds) }}</td>
                                <td :class="alloc.capital_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(alloc.capital_gain) }}</td>
                                <td>{{ alloc.holding_days }}</td>
                                <td>{{ alloc.discount_eligible ? 'yes' : 'no' }}</td>
                              </tr>
                              <tr v-if="disposal.hifo_preview.allocations.length > 1" class="font-weight-bold">
                                <td></td><td></td><td></td><td></td><td></td>
                                <td :class="disposal.hifo_preview.total_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(disposal.hifo_preview.total_gain) }}</td>
                                <td></td><td></td>
                              </tr>
                            </table>
                          </v-window-item>

                          <v-window-item v-if="disposal.has_persisted_allocation" value="saved">
                            <table class="detail-table mb-4">
                              <tr>
                                <th>buy date</th><th>qty</th><th>unit cost</th><th>cost base</th><th>proceeds</th><th>gain</th><th>days held</th><th>discount eligible</th>
                              </tr>
                              <tr v-for="alloc in disposal.persisted_allocation.allocations" :key="'saved-' + alloc.buy_txn_id">
                                <td>{{ showDateFormat(alloc.buy_date) }}</td>
                                <td>{{ alloc.qty.toLocaleString() }} / {{ parcelTotalQty(disposal, alloc.buy_txn_id).toLocaleString() }}</td>
                                <td>{{ formatCurrency(alloc.unit_cost) }}</td>
                                <td>{{ formatCurrency(alloc.cost_base) }}</td>
                                <td>{{ formatCurrency(alloc.proceeds) }}</td>
                                <td :class="alloc.capital_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(alloc.capital_gain) }}</td>
                                <td>{{ alloc.holding_days }}</td>
                                <td>{{ alloc.discount_eligible ? 'yes' : 'no' }}</td>
                              </tr>
                              <tr v-if="disposal.persisted_allocation.allocations.length > 1" class="font-weight-bold">
                                <td></td><td></td><td></td><td></td><td></td>
                                <td :class="disposal.persisted_allocation.total_gain >= 0 ? 'text-green' : 'text-red'">{{ formatCurrency(disposal.persisted_allocation.total_gain) }}</td>
                                <td></td><td></td>
                              </tr>
                            </table>
                          </v-window-item>
                        </v-window>

                        <!-- prior unallocated sells warning -->
                        <v-alert v-if="disposal.prior_unallocated_sells.length > 0" type="info" variant="tonal" density="compact" class="mt-3">
                          {{ disposal.prior_unallocated_sells.length }} prior sell(s) for {{ disposal.ticker }} have no saved allocation
                        </v-alert>

                        <div class="text-subtitle-2 mt-3 text-left">open units after disposal: {{ (disposal.open_units_at_disposal - disposal.sell_txn.qty).toLocaleString() }}</div>
                      </v-card-text>
                    </v-card>
                  </td>
                </tr>
              </template>
            </table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <v-row v-if="report && report.disposals.length === 0">
    <v-col cols="12">
      <v-card :elevation="1">
        <v-card-text class="text-left text-grey">no sell transactions found in this date range</v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- ticker transaction timelines -->
  <v-row v-if="report && report.ticker_timelines && report.ticker_timelines.length > 0">
    <v-col cols="12" v-for="timeline in report.ticker_timelines" :key="'tl-' + timeline.ticker">
      <v-card :elevation="1">
        <v-card-item @click="toggleTimeline(timeline.ticker)" style="cursor: pointer;">
          <template v-slot:prepend>
            <v-icon size="small">{{ expandedTimelines[timeline.ticker] ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </template>
          <v-card-title class="text-left">{{ timeline.ticker }} transaction history</v-card-title>
          <template v-slot:append>
            <span class="text-caption text-grey">{{ timelineSellCount(timeline) }} disposal(s) allocated</span>
          </template>
        </v-card-item>
        <template v-if="expandedTimelines[timeline.ticker]">
          <v-divider></v-divider>
          <v-card-text>
            <!-- legend -->
            <div class="d-flex align-center mb-3 text-caption text-grey">
              <template v-for="(sellInfo, idx) in timelineSells(timeline)" :key="'legend-' + sellInfo.sell_txn_id">
                <span class="timeline-legend-dot" :style="{ backgroundColor: allocationColor(idx) }"></span>
                <span class="mr-4">sold {{ showDateFormat(sellInfo.sell_date) }}</span>
              </template>
            </div>
            <v-container>
              <table class="detail-table">
                <tr>
                  <th></th>
                  <th>date</th>
                  <th>type</th>
                  <th>qty</th>
                  <th>price</th>
                  <th>net amount</th>
                  <th>allocated</th>
                </tr>
                <tr v-for="txn in timeline.txns" :key="'tltxn-' + txn.id"
                    :class="timelineRowClass(txn, timeline)">
                  <td>
                    <span v-if="isSellWithAllocation(txn, timeline)" class="timeline-dot" :style="{ backgroundColor: sellColor(txn.id, timeline) }"></span>
                    <span v-for="(alloc, aidx) in getBuyAllocations(txn, timeline)" :key="'tdot-' + aidx"
                          class="timeline-dot" :style="{ backgroundColor: sellColor(alloc.sell_txn_id, timeline) }"></span>
                  </td>
                  <td>{{ showDateFormat(txn.txn_date) }}</td>
                  <td>{{ txn.txn_type }}</td>
                  <td>{{ txn.qty.toLocaleString() }}</td>
                  <td>{{ formatCurrency(txn.price) }}</td>
                  <td>{{ formatCurrency(txn.net_amount) }}</td>
                  <td>
                    <template v-if="isSellWithAllocation(txn, timeline)">
                      <span class="font-weight-bold">disposal</span>
                    </template>
                    <template v-for="(alloc, aidx) in getBuyAllocations(txn, timeline)" :key="'badge-' + aidx">
                      <span class="timeline-badge" :style="{ backgroundColor: sellColor(alloc.sell_txn_id, timeline) }">{{ alloc.qty }}/{{ alloc.buy_qty }}</span>
                    </template>
                  </td>
                </tr>
              </table>
            </v-container>
          </v-card-text>
        </template>
      </v-card>
    </v-col>
  </v-row>

  <!-- allocation modal -->
  <CgtAllocationModalPopup
    :show="showAllocationModal"
    :disposal="selectedDisposal"
    @close="showAllocationModal = false"
    @saved="onAllocationSaved"
  />
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations } from 'vuex';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import SpaceGroteskRegular from '@/assets/SpaceGroteskFont.js';
import CgtAllocationModalPopup from './modals/CgtAllocationModalPopup.vue';

export default {
  components: { CgtAllocationModalPopup },
  data() {
    return {
      brokerAccounts: [],
      selectedBrokerAccounts: [],
      selectedFY: null,
      startDate: '',
      endDate: '',
      report: null,
      loading: false,
      bootstrapping: false,
      expandedRows: {},
      isBaseCurrency: true,
      showAllocationModal: false,
      selectedDisposal: null,
      expandedTimelines: {},
      activeTab: {},
      carryForwardLosses: 0,
      cfSaved: false,
      cfEditing: false,
      offPlatformEntries: [],
      offPlatformExpanded: false,
      offPlatformEditing: null,
      offPlatformForm: { description: '', discount_gains: 0, non_discount_gains: 0, capital_losses: 0 }
    };
  },
  async created() {
    this.getBrokerAccounts();
  },
  computed: {
    ...mapState({ token: "token" }),
    hasUnallocatedPriorSells() {
      if (!this.report) return false;
      return this.report.disposals.some(d => d.prior_unallocated_sells.length > 0);
    },
    financialYears() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth(); // 0-indexed
      // if we're past June, current FY ends next year
      const latestEndYear = currentMonth >= 6 ? currentYear + 1 : currentYear;
      const years = [];
      for (let endYear = latestEndYear; endYear >= latestEndYear - 5; endYear--) {
        years.push('FY' + (endYear - 1).toString().slice(2) + '/' + endYear.toString().slice(2));
      }
      return years;
    },
    formatCarryForwardDisplay() {
      if (this.cfEditing) return this.carryForwardLosses === 0 ? '0' : (this.carryForwardLosses || '');
      if (this.carryForwardLosses == null) return '0.00';
      return this.carryForwardLosses.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    atoSummary() {
      if (!this.report) return null;

      let totalDiscountGains = 0;
      let totalNonDiscountGains = 0;
      let totalCapitalLosses = 0;

      for (const d of this.report.disposals) {
        const source = d.has_persisted_allocation ? d.persisted_allocation : d.fifo_preview;
        for (const alloc of source.allocations) {
          if (alloc.gain_type === 'capital_loss') {
            totalCapitalLosses += alloc.capital_gain;
          } else if (alloc.gain_type === 'discount_gain') {
            totalDiscountGains += alloc.capital_gain;
          } else {
            totalNonDiscountGains += alloc.capital_gain;
          }
        }
      }

      // add off-platform entries
      for (const entry of this.offPlatformEntries) {
        totalDiscountGains += parseFloat(entry.discount_gains) || 0;
        totalNonDiscountGains += parseFloat(entry.non_discount_gains) || 0;
        totalCapitalLosses -= parseFloat(entry.capital_losses) || 0;
      }

      // step 2: current year losses (optimal: non-discount first)
      let lossesRemaining = Math.abs(totalCapitalLosses);
      const lossesToNonDiscount = Math.min(lossesRemaining, totalNonDiscountGains);
      lossesRemaining -= lossesToNonDiscount;
      const lossesToDiscount = Math.min(lossesRemaining, totalDiscountGains);
      lossesRemaining -= lossesToDiscount;

      const nonDiscountAfterLosses = totalNonDiscountGains - lossesToNonDiscount;
      const discountAfterLosses = totalDiscountGains - lossesToDiscount;

      // step 3: carry-forward losses
      let cfRemaining = this.carryForwardLosses || 0;
      const cfToNonDiscount = Math.min(cfRemaining, nonDiscountAfterLosses);
      cfRemaining -= cfToNonDiscount;
      const cfToDiscount = Math.min(cfRemaining, discountAfterLosses);
      cfRemaining -= cfToDiscount;

      const nonDiscountFinal = nonDiscountAfterLosses - cfToNonDiscount;
      const discountFinal = discountAfterLosses - cfToDiscount;

      // step 4: discount
      const discountAmount = discountFinal * 0.5;

      // step 5: net
      const netCapitalGain = nonDiscountFinal + discountAmount;
      const netLossToCarry = lossesRemaining + cfRemaining;

      return {
        totalDiscountGains, totalNonDiscountGains, totalCapitalLosses,
        lossesToNonDiscount, lossesToDiscount,
        nonDiscountAfterLosses, discountAfterLosses,
        cfToNonDiscount, cfToDiscount,
        nonDiscountFinal, discountFinal,
        discountAmount,
        netCapitalGain, netLossToCarry
      };
    }
  },
  watch: {
    selectedBrokerAccounts() {
      this.loadCarryForwardLoss();
    }
  },
  methods: {
    ...mapMutations(["showSnackbar"]),
    activeSource(disposal) {
      return disposal.has_persisted_allocation ? disposal.persisted_allocation : disposal.fifo_preview;
    },
    parcelTotalQty(disposal, buyTxnId) {
      const parcel = disposal.available_parcels.find(p => p.buy_txn.id === buyTxnId);
      return parcel ? parcel.total_qty : 0;
    },
    disposalGainType(disposal) {
      const source = this.activeSource(disposal);
      // determine dominant gain type from allocations
      let discountGain = 0;
      let nonDiscountGain = 0;
      let loss = 0;
      for (const a of source.allocations) {
        if (a.gain_type === 'discount_gain') discountGain += a.capital_gain;
        else if (a.gain_type === 'non_discount_gain') nonDiscountGain += a.capital_gain;
        else loss += a.capital_gain;
      }
      if (source.total_gain < 0) return { label: 'loss', color: 'red' };
      if (discountGain > nonDiscountGain) return { label: 'discount', color: 'green' };
      return { label: 'non-discount', color: 'orange' };
    },
    onFYSelect(val) {
      if (!val) return;
      // parse "FY24/25" → start 2024-07-01, end 2025-06-30
      const parts = val.replace('FY', '').split('/');
      const startYear = 2000 + parseInt(parts[0]);
      const endYear = 2000 + parseInt(parts[1]);
      this.startDate = startYear + '-07-01';
      this.endDate = endYear + '-06-30';
      this.loadCarryForwardLoss();
      this.loadOffPlatformEntries();
      if (this.selectedBrokerAccounts.length > 0) this.getDisposals();
    },
    carryForwardKey() {
      if (!this.selectedFY || this.selectedBrokerAccounts.length === 0) return null;
      const ids = [...this.selectedBrokerAccounts].sort((a, b) => a - b).join(',');
      return this.selectedFY + '_' + ids;
    },
    async loadCarryForwardLoss() {
      const key = this.carryForwardKey();
      if (!key) return;
      try {
        const response = await axios.get(
          '/reports/cgtdisposals/carryloss/' + encodeURIComponent(key),
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.carryForwardLosses = response.data.value || 0;
      } catch {
        // ignore
      }
    },
    parseCarryForwardInput(val) {
      // allow typing by storing raw parsed number
      const num = parseFloat(String(val).replace(/,/g, ''));
      if (!isNaN(num)) this.carryForwardLosses = num;
      else if (val === '' || val == null) this.carryForwardLosses = 0;
    },
    onCarryForwardBlur() {
      this.cfEditing = false;
    },
    async saveCarryForwardLoss() {
      const key = this.carryForwardKey();
      if (!key) return;
      try {
        await axios.post(
          '/reports/cgtdisposals/carryloss/' + encodeURIComponent(key),
          { value: this.carryForwardLosses },
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.cfSaved = true;
        setTimeout(() => { this.cfSaved = false; }, 2000);
      } catch {
        this.showSnackbar({ message: 'Failed to save carry-forward loss' });
      }
    },
    async loadOffPlatformEntries() {
      if (!this.selectedFY) return;
      try {
        const response = await axios.get(
          '/reports/cgtdisposals/offplatform/' + encodeURIComponent(this.selectedFY),
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.offPlatformEntries = response.data;
      } catch {
        // ignore
      }
    },
    startOffPlatformAdd() {
      this.offPlatformForm = { description: '', discount_gains: 0, non_discount_gains: 0, capital_losses: 0 };
      this.offPlatformEditing = 'new';
      this.offPlatformExpanded = true;
    },
    startOffPlatformEdit(entry) {
      this.offPlatformForm = {
        id: entry.id,
        description: entry.description,
        discount_gains: parseFloat(entry.discount_gains) || 0,
        non_discount_gains: parseFloat(entry.non_discount_gains) || 0,
        capital_losses: parseFloat(entry.capital_losses) || 0
      };
      this.offPlatformEditing = entry.id;
    },
    cancelOffPlatformEdit() {
      this.offPlatformEditing = null;
    },
    async saveOffPlatformEntry() {
      if (!this.selectedFY || !this.offPlatformForm.description) return;
      try {
        await axios.post(
          '/reports/cgtdisposals/offplatform/' + encodeURIComponent(this.selectedFY),
          this.offPlatformForm,
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.offPlatformEditing = null;
        await this.loadOffPlatformEntries();
      } catch {
        this.showSnackbar({ message: 'Failed to save off-platform entry' });
      }
    },
    async deleteOffPlatformEntry(id) {
      try {
        await axios.delete(
          '/reports/cgtdisposals/offplatform/' + id,
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        await this.loadOffPlatformEntries();
      } catch {
        this.showSnackbar({ message: 'Failed to delete off-platform entry' });
      }
    },
    async getBrokerAccounts() {
      try {
        const response = await axios.get("/securities/brokeraccounts/", {
          headers: { Authorization: `Bearer ${this.token}` }
        });
        this.brokerAccounts = response.data.map(a => ({
          id: a.id,
          institution: a.institution,
          label: a.institution + ' - ' + a.account_name
        }));
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load broker accounts' });
      }
    },
    async getDisposals() {
      this.loading = true;
      this.report = null;
      this.expandedRows = {};
      this.expandedTimelines = {};
      this.activeTab = {};
      this.isBaseCurrency = true;
      try {
        const response = await axios.get(
          "/reports/cgtdisposals/" + this.selectedBrokerAccounts.join(',') + "?start_date=" + this.startDate + "&end_date=" + this.endDate,
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        this.report = response.data;
      } catch (error) {
        this.showSnackbar({ message: 'Failed to load CGT disposals report' });
      }
      this.loading = false;
    },
    async bootstrapAllocations() {
      this.bootstrapping = true;
      try {
        for (const baId of this.selectedBrokerAccounts) {
          await axios.post(
            "/reports/cgtdisposals/bootstrap/" + baId,
            {},
            { headers: { Authorization: `Bearer ${this.token}` } }
          );
        }
        await this.getDisposals();
      } catch (error) {
        this.showSnackbar({ message: 'Failed to auto-allocate' });
      }
      this.bootstrapping = false;
    },
    async deleteAllocation(sell_txn_id) {
      try {
        await axios.delete(
          "/reports/cgtdisposals/allocate/" + sell_txn_id,
          { headers: { Authorization: `Bearer ${this.token}` } }
        );
        await this.getDisposals();
      } catch (error) {
        this.showSnackbar({ message: 'Failed to delete allocation' });
      }
    },
    toggleExpand(index) {
      this.expandedRows = { ...this.expandedRows, [index]: !this.expandedRows[index] };
      if (!this.activeTab[index]) {
        const disposal = this.report.disposals[index];
        const defaultTab = disposal.has_persisted_allocation ? 'saved' : 'fifo';
        this.activeTab = { ...this.activeTab, [index]: defaultTab };
      }
    },
    openAllocationModal(disposal) {
      this.selectedDisposal = disposal;
      this.showAllocationModal = true;
    },
    async onAllocationSaved() {
      const savedSellTxnId = this.selectedDisposal?.sell_txn?.id;
      this.showAllocationModal = false;
      await this.getDisposals();
      if (savedSellTxnId && this.report) {
        const index = this.report.disposals.findIndex(d => d.sell_txn.id === savedSellTxnId);
        if (index !== -1) {
          this.expandedRows = { ...this.expandedRows, [index]: true };
          this.activeTab = { ...this.activeTab, [index]: 'saved' };
        }
      }
    },
    showDateFormat(rawDate) {
      return moment(rawDate).format('DD/MM/YYYY');
    },
    // timeline helper methods
    toggleTimeline(ticker) {
      this.expandedTimelines = { ...this.expandedTimelines, [ticker]: !this.expandedTimelines[ticker] };
    },
    allocationColors() {
      return ['#1976D2', '#E65100', '#7B1FA2', '#C62828', '#00838F', '#558B2F'];
    },
    allocationColor(index) {
      const colors = this.allocationColors();
      return colors[index % colors.length];
    },
    timelineSells(timeline) {
      // unique sell txn ids from allocations
      const seen = new Set();
      const sells = [];
      for (const alloc of timeline.allocations) {
        if (!seen.has(alloc.sell_txn_id)) {
          seen.add(alloc.sell_txn_id);
          sells.push({ sell_txn_id: alloc.sell_txn_id, sell_date: alloc.sell_date });
        }
      }
      return sells;
    },
    timelineSellCount(timeline) {
      return this.timelineSells(timeline).length;
    },
    sellColorIndex(sell_txn_id, timeline) {
      const sells = this.timelineSells(timeline);
      return sells.findIndex(s => s.sell_txn_id === sell_txn_id);
    },
    sellColor(sell_txn_id, timeline) {
      return this.allocationColor(this.sellColorIndex(sell_txn_id, timeline));
    },
    isSellWithAllocation(txn, timeline) {
      return txn.txn_type === 'sell' && timeline.allocations.some(a => a.sell_txn_id === txn.id);
    },
    getBuyAllocations(txn, timeline) {
      if (txn.txn_type === 'sell') return [];
      return timeline.allocations.filter(a => a.buy_txn_id === txn.id);
    },
    timelineRowClass(txn, timeline) {
      if (this.isSellWithAllocation(txn, timeline)) return 'timeline-sell-row';
      if (this.getBuyAllocations(txn, timeline).length > 0) return 'timeline-allocated-row';
      return '';
    },
    formatCurrency(val) {
      if (val == null) return '-';
      return val.toLocaleString("en", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    brokerAccountLabel(broker_account_id) {
      const account = this.brokerAccounts.find(a => a.id === broker_account_id);
      return account ? account.institution : '';
    },
    selectedAccountLabel() {
      return this.selectedBrokerAccounts
        .map(id => this.brokerAccounts.find(a => a.id === id))
        .filter(a => a)
        .map(a => a.label)
        .join(', ');
    },
    async exportPDF() {
      const doc = new jsPDF();

      doc.addFileToVFS('SpaceGrotesk-Regular.ttf', SpaceGroteskRegular);
      doc.addFont('SpaceGrotesk-Regular.ttf', 'SpaceGrotesk', 'normal');
      doc.setFont('SpaceGrotesk');

      const label = this.selectedAccountLabel();

      try {
        const response = await fetch('/w_icon_medium.png');
        const blob = await response.blob();
        const dataUrl = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(dataUrl, 'PNG', 14, 10, 12, 12);
      } catch (e) {
        // continue without logo
      }

      doc.setFontSize(18);
      doc.text('wellfi', 29, 19);

      doc.setFontSize(12);
      doc.text('CGT Disposals Report', 14, 30);
      doc.setFontSize(10);
      doc.setFontSize(10);
      const dateRange = moment(this.startDate).format('DD-MM-YYYY') + ' to ' + moment(this.endDate).format('DD-MM-YYYY');
      doc.text(dateRange, 14, 36);
      const accountLabels = this.selectedBrokerAccounts
        .map(id => this.brokerAccounts.find(a => a.id === id))
        .filter(a => a)
        .map(a => a.label);
      doc.setFontSize(9);
      accountLabels.forEach((line, i) => {
        doc.text(line, 14, 42 + (i * 4));
      });

      // ATO net capital gain calculation
      const s = this.atoSummary;
      doc.setFontSize(11);
      let sy = 42 + (accountLabels.length * 4) + 4;
      doc.text('Net Capital Gain Calculation', 14, sy);

      const fc = (v) => v != null ? v.toFixed(2) : '0.00';

      // build rows with vertical calculations as sub-rows
      const tableRows = [];
      const rowMeta = [];

      function addRow(label, value, meta = {}) {
        tableRows.push([label, value]);
        rowMeta.push(meta);
      }

      function addCalc(label, top, mid, result) {
        addRow(label, top, { isCalcTop: true });
        addRow('', '- ' + mid, { isCalcMid: true });
        addRow('', result, { isCalcResult: true });
      }

      if (this.offPlatformEntries.length > 0) {
        addRow('Off-platform gains', '', { isHeader: true });
        for (const entry of this.offPlatformEntries) {
          addRow('    ' + entry.description, '');
          addRow('        Discount gains', fc(parseFloat(entry.discount_gains)));
          addRow('        Non-discount gains', fc(parseFloat(entry.non_discount_gains)));
          addRow('        Capital losses', parseFloat(entry.capital_losses) ? '-' + fc(parseFloat(entry.capital_losses)) : fc(0), { isLoss: true });
        }
      }

      addRow('1. Classify gains', '', { isHeader: true });
      addRow('    Discount gains (held >12 months)', fc(s.totalDiscountGains));
      addRow('    Non-discount gains (held <=12 months)', fc(s.totalNonDiscountGains));
      addRow('    Capital losses', fc(s.totalCapitalLosses), { isLoss: true });
      addRow('2. Apply current year losses', '', { isHeader: true });
      addCalc('    Non-discount gains', fc(s.totalNonDiscountGains), fc(s.lossesToNonDiscount), fc(s.nonDiscountAfterLosses));
      addCalc('    Discount gains', fc(s.totalDiscountGains), fc(s.lossesToDiscount), fc(s.discountAfterLosses));
      addRow('3. Apply carry-forward losses', '', { isHeader: true });
      addRow('    Prior year losses', fc(this.carryForwardLosses || 0));
      addCalc('    Non-discount gains', fc(s.nonDiscountAfterLosses), fc(s.cfToNonDiscount), fc(s.nonDiscountFinal));
      addCalc('    Discount gains', fc(s.discountAfterLosses), fc(s.cfToDiscount), fc(s.discountFinal));
      addRow('4. Apply CGT discount (50%)', '', { isHeader: true });
      addRow('    50% discount on ' + fc(s.discountFinal), '(' + fc(s.discountAmount) + ')');
      addRow('5. Net capital gain (taxable)', fc(s.netCapitalGain), { isResult: true });

      if (s.netLossToCarry > 0) {
        addRow('    Net capital loss to carry forward', fc(s.netLossToCarry));
      }

      autoTable(doc, {
        startY: sy + 4,
        body: tableRows,
        styles: { fontSize: 8, font: 'SpaceGrotesk', cellPadding: 1.5 },
        columnStyles: {
          0: { cellWidth: 120 },
          1: { cellWidth: 40, halign: 'right' }
        },
        didParseCell: (data) => {
          if (data.section !== 'body') return;
          const meta = rowMeta[data.row.index];
          if (!meta) return;
          if (meta.isHeader) data.cell.styles.fontStyle = 'bold';
          if (meta.isResult) data.cell.styles.fontStyle = 'bold';
          if (meta.isCalcMid && data.column.index === 1) data.cell.styles.textColor = [200, 0, 0];
          if (meta.isLoss && data.column.index === 1) data.cell.styles.textColor = [200, 0, 0];
        },
        didDrawCell: (data) => {
          if (data.section !== 'body' || data.column.index !== 1) return;
          const meta = rowMeta[data.row.index];
          if (meta && meta.isCalcResult) {
            doc.setDrawColor(180, 180, 180);
            doc.setLineWidth(0.3);
            doc.line(data.cell.x + 2, data.cell.y + 0.5, data.cell.x + data.cell.width - 2, data.cell.y + 0.5);
          }
        },
        theme: 'plain',
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.25
      });

      sy = doc.lastAutoTable.finalY + 8;

      // disposals summary table
      doc.setFontSize(11);
      doc.text('Disposals', 14, sy);

      const headers = ['Date', 'Broker', 'Ticker', 'Ccy', 'Qty', 'Proceeds', 'Gain', 'Type', 'Method'];
      const rows = this.report.disposals.map(d => {
        const source = this.activeSource(d);
        const gainType = this.disposalGainType(d);
        const ccy = d.sell_txn.currency && d.sell_txn.currency !== 'AUD' ? 'AUD' : (d.sell_txn.currency || 'AUD');
        return [
          this.showDateFormat(d.sell_txn.txn_date),
          this.brokerAccountLabel(d.sell_txn.broker_account_id),
          d.ticker,
          ccy,
          d.sell_txn.qty,
          this.formatCurrency(source.allocations.reduce((s, a) => s + a.proceeds, 0)),
          this.formatCurrency(source.total_gain),
          gainType.label,
          d.has_persisted_allocation ? source.method : '-'
        ];
      });

      autoTable(doc, {
        startY: sy + 6,
        head: [headers],
        body: rows,
        styles: { fontSize: 8, font: 'SpaceGrotesk' },
        headStyles: { fillColor: [0, 0, 0], font: 'SpaceGrotesk' }
      });

      // saved allocation detail tables + ticker timelines grouped by ticker
      const savedDisposals = this.report.disposals.filter(d => d.has_persisted_allocation);
      if (savedDisposals.length > 0) {
        let startY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(11);
        doc.text('Allocation Details', 14, startY);
        startY += 6;

        const detailHeaders = ['Buy Date', 'Qty', 'Unit Cost', 'Cost Base', 'Proceeds', 'Gain', 'Days Held', 'Discount Eligible'];
        const tlHeaders = ['', 'Date', 'Type', 'Qty', 'Price', 'Net Amount', 'Allocated'];

        // helper: hex color to [r, g, b] array
        const hexToRgb = (hex) => [
          parseInt(hex.slice(1, 3), 16),
          parseInt(hex.slice(3, 5), 16),
          parseInt(hex.slice(5, 7), 16)
        ];

        // group saved disposals by ticker to know when to render timeline
        const tickerOrder = [];
        const tickerDisposalMap = {};
        for (const d of savedDisposals) {
          if (!tickerDisposalMap[d.ticker]) {
            tickerDisposalMap[d.ticker] = [];
            tickerOrder.push(d.ticker);
          }
          tickerDisposalMap[d.ticker].push(d);
        }

        // build timeline lookup by ticker
        const timelineMap = {};
        if (this.report.ticker_timelines) {
          for (const tl of this.report.ticker_timelines) {
            timelineMap[tl.ticker] = tl;
          }
        }

        for (let ti = 0; ti < tickerOrder.length; ti++) {
          const ticker = tickerOrder[ti];
          const tickerDisposals = tickerDisposalMap[ticker];

          // render each disposal's saved allocation detail
          for (const d of tickerDisposals) {
            doc.setFontSize(9);
            doc.text(d.ticker + ' sold ' + this.showDateFormat(d.sell_txn.txn_date) + ' (' + d.persisted_allocation.method + ')', 14, startY);
            startY += 2;

            const detailRows = d.persisted_allocation.allocations.map(a => [
              this.showDateFormat(a.buy_date),
              a.qty,
              a.unit_cost.toFixed(2),
              a.cost_base.toFixed(2),
              a.proceeds.toFixed(2),
              a.capital_gain.toFixed(2),
              a.holding_days,
              a.discount_eligible ? 'Yes' : 'No'
            ]);

            if (d.persisted_allocation.allocations.length > 1) {
              detailRows.push(['', '', '', '', '', d.persisted_allocation.total_gain.toFixed(2), '', '']);
            }

            autoTable(doc, {
              startY,
              head: [detailHeaders],
              body: detailRows,
              styles: { fontSize: 7, font: 'SpaceGrotesk' },
              headStyles: { fillColor: [80, 80, 80], font: 'SpaceGrotesk' },
              didParseCell: (data) => {
                if (data.row.index === detailRows.length - 1 && data.section === 'body' && d.persisted_allocation.allocations.length > 1) {
                  data.cell.styles.fontStyle = 'bold';
                }
              }
            });

            startY = doc.lastAutoTable.finalY + 4;
          }

          // render this ticker's transaction timeline right after its allocation details
          const timeline = timelineMap[ticker];
          if (timeline) {
            const sells = this.timelineSells(timeline);
            const sellIds = new Set(sells.map(s => s.sell_txn_id));
            const buyAllocMap = {};
            for (const alloc of timeline.allocations) {
              if (!buyAllocMap[alloc.buy_txn_id]) buyAllocMap[alloc.buy_txn_id] = [];
              buyAllocMap[alloc.buy_txn_id].push(alloc);
            }

            // legend line
            doc.setFontSize(7);
            let legendX = 14;
            for (let si = 0; si < sells.length; si++) {
              const rgb = hexToRgb(this.allocationColor(si));
              doc.setFillColor(rgb[0], rgb[1], rgb[2]);
              doc.circle(legendX + 1.5, startY - 1, 1.5, 'F');
              legendX += 4;
              const legendLabel = 'sold ' + moment(sells[si].sell_date).format('DD/MM/YYYY');
              doc.text(legendLabel, legendX, startY);
              legendX += doc.getTextWidth(legendLabel) + 4;
            }
            startY += 3;

            const tlRows = timeline.txns.map(txn => {
              let allocText = '';
              if (txn.txn_type === 'sell' && sellIds.has(txn.id)) {
                allocText = 'disposal';
              } else if (buyAllocMap[txn.id]) {
                allocText = buyAllocMap[txn.id].map(a => a.qty + '/' + a.buy_qty).join(', ');
              }
              return [
                '',  // dot column — drawn via didDrawCell
                moment(txn.txn_date).format('DD/MM/YYYY'),
                txn.txn_type,
                txn.qty,
                txn.price != null ? txn.price.toFixed(2) : '-',
                txn.net_amount != null ? txn.net_amount.toFixed(2) : '-',
                allocText
              ];
            });

            autoTable(doc, {
              startY,
              head: [tlHeaders],
              body: tlRows,
              styles: { fontSize: 7, font: 'SpaceGrotesk' },
              headStyles: { fillColor: [80, 80, 80], font: 'SpaceGrotesk' },
              columnStyles: { 0: { cellWidth: 8 } },
              didParseCell: (data) => {
                if (data.section !== 'body') return;
                const txn = timeline.txns[data.row.index];
                if (!txn) return;
                if (txn.txn_type === 'sell' && sellIds.has(txn.id)) {
                  data.cell.styles.fontStyle = 'bold';
                }
                if (data.column.index === 0) {
                  data.cell.text = [''];
                }
                if (data.column.index === 6 && buyAllocMap[txn.id]) {
                  data.cell.text = [''];
                }
              },
              didDrawCell: (data) => {
                if (data.section !== 'body') return;
                const txn = timeline.txns[data.row.index];
                if (!txn) return;

                // draw dots in the first column
                if (data.column.index === 0) {
                  const cx = data.cell.x + data.cell.width / 2;
                  const cy = data.cell.y + data.cell.height / 2;

                  if (txn.txn_type === 'sell' && sellIds.has(txn.id)) {
                    const rgb = hexToRgb(this.sellColor(txn.id, timeline));
                    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
                    doc.circle(cx, cy, 1.5, 'F');
                  } else if (buyAllocMap[txn.id]) {
                    const allocs = buyAllocMap[txn.id];
                    const dotSpacing = 4;
                    const dotStartX = cx - ((allocs.length - 1) * dotSpacing) / 2;
                    for (let i = 0; i < allocs.length; i++) {
                      const rgb = hexToRgb(this.sellColor(allocs[i].sell_txn_id, timeline));
                      doc.setFillColor(rgb[0], rgb[1], rgb[2]);
                      doc.circle(dotStartX + i * dotSpacing, cy, 1.5, 'F');
                    }
                  }
                }

                // draw colored badges in the allocated column for buy rows
                if (data.column.index === 6 && buyAllocMap[txn.id]) {
                  const allocs = buyAllocMap[txn.id];
                  let bx = data.cell.x + 2;
                  const by = data.cell.y + data.cell.height / 2;
                  doc.setFontSize(6);
                  for (const alloc of allocs) {
                    const label = alloc.qty + '/' + alloc.buy_qty;
                    const tw = doc.getTextWidth(label);
                    const badgeW = tw + 3;
                    const badgeH = 3.5;
                    const rgb = hexToRgb(this.sellColor(alloc.sell_txn_id, timeline));
                    doc.setFillColor(rgb[0], rgb[1], rgb[2]);
                    doc.roundedRect(bx, by - badgeH / 2, badgeW, badgeH, 1.5, 1.5, 'F');
                    doc.setTextColor(255, 255, 255);
                    doc.setFont('SpaceGrotesk', 'normal');
                    doc.text(label, bx + 1.5, by + 1);
                    doc.setTextColor(0, 0, 0);
                    bx += badgeW + 2;
                  }
                  doc.setFontSize(7);
                }
              }
            });

            startY = doc.lastAutoTable.finalY + 14;
          }
        }
      }

      doc.save('cgt_disposals_' + this.startDate + '_' + this.endDate + '.pdf');
    },
    exportCSV() {
      const label = this.selectedAccountLabel();
      const s = this.atoSummary;
      const lines = [];

      lines.push('CGT Disposals Report');
      lines.push(moment(this.startDate).format('DD-MM-YYYY') + ' to ' + moment(this.endDate).format('DD-MM-YYYY'));
      const accountLabels = this.selectedBrokerAccounts
        .map(id => this.brokerAccounts.find(a => a.id === id))
        .filter(a => a)
        .map(a => a.label);
      accountLabels.forEach(l => lines.push(l));
      lines.push('');
      lines.push('Net Capital Gain Calculation');
      lines.push(',');
      if (this.offPlatformEntries.length > 0) {
        lines.push('Off-platform gains,');
        lines.push(['Description', 'Discount Gains', 'Non-Discount Gains', 'Capital Losses'].join(','));
        for (const entry of this.offPlatformEntries) {
          lines.push(['"' + entry.description + '"', parseFloat(entry.discount_gains).toFixed(2), parseFloat(entry.non_discount_gains).toFixed(2), parseFloat(entry.capital_losses).toFixed(2)].join(','));
        }
        lines.push(',');
      }
      lines.push('1. Classify gains,');
      lines.push('    Discount gains (held >12 months),' + s.totalDiscountGains.toFixed(2));
      lines.push('    Non-discount gains (held <=12 months),' + s.totalNonDiscountGains.toFixed(2));
      lines.push('    Capital losses,' + s.totalCapitalLosses.toFixed(2));
      lines.push('2. Apply current year losses,');
      lines.push('    Non-discount gains,' + s.totalNonDiscountGains.toFixed(2));
      lines.push('    ,' + '- ' + s.lossesToNonDiscount.toFixed(2));
      lines.push('    ,' + s.nonDiscountAfterLosses.toFixed(2));
      lines.push('    Discount gains,' + s.totalDiscountGains.toFixed(2));
      lines.push('    ,' + '- ' + s.lossesToDiscount.toFixed(2));
      lines.push('    ,' + s.discountAfterLosses.toFixed(2));
      lines.push('3. Apply carry-forward losses,');
      lines.push('    Prior year losses,' + (this.carryForwardLosses || 0).toFixed(2));
      lines.push('    Non-discount gains,' + s.nonDiscountAfterLosses.toFixed(2));
      lines.push('    ,' + '- ' + s.cfToNonDiscount.toFixed(2));
      lines.push('    ,' + s.nonDiscountFinal.toFixed(2));
      lines.push('    Discount gains,' + s.discountAfterLosses.toFixed(2));
      lines.push('    ,' + '- ' + s.cfToDiscount.toFixed(2));
      lines.push('    ,' + s.discountFinal.toFixed(2));
      lines.push('4. Apply CGT discount (50%),');
      lines.push('    50% discount on ' + s.discountFinal.toFixed(2) + ',(' + s.discountAmount.toFixed(2) + ')');
      lines.push('5. Net capital gain (taxable),' + s.netCapitalGain.toFixed(2));
      if (s.netLossToCarry > 0) {
        lines.push('    Net capital loss to carry forward,' + s.netLossToCarry.toFixed(2));
      }
      lines.push('');
      lines.push('Disposals');
      lines.push(['Date', 'Broker', 'Ticker', 'Ccy', 'Qty', 'Proceeds', 'FIFO Gain', 'LIFO Gain', 'LOFO Gain', 'HIFO Gain', 'Saved Method', 'Saved Gain'].join(','));

      this.report.disposals.forEach(d => {
        const proceeds = d.fifo_preview.allocations.reduce((s, a) => s + a.proceeds, 0);
        const ccy = d.sell_txn.currency && d.sell_txn.currency !== 'AUD' ? 'AUD' : (d.sell_txn.currency || 'AUD');
        lines.push([
          this.showDateFormat(d.sell_txn.txn_date),
          '"' + this.brokerAccountLabel(d.sell_txn.broker_account_id) + '"',
          d.ticker,
          ccy,
          d.sell_txn.qty,
          proceeds.toFixed(2),
          d.fifo_preview.total_gain.toFixed(2),
          d.lifo_preview.total_gain.toFixed(2),
          d.lofo_preview.total_gain.toFixed(2),
          d.hifo_preview.total_gain.toFixed(2),
          d.has_persisted_allocation ? d.persisted_allocation.method : '',
          d.has_persisted_allocation ? d.persisted_allocation.total_gain.toFixed(2) : ''
        ].join(','));
      });

      // saved allocation detail
      const savedDisposals = this.report.disposals.filter(d => d.has_persisted_allocation);
      if (savedDisposals.length > 0) {
        lines.push('');
        lines.push('Saved Allocation Detail');

        for (const d of savedDisposals) {
          lines.push('');
          lines.push(d.ticker + ' sold ' + this.showDateFormat(d.sell_txn.txn_date) + ' (' + d.persisted_allocation.method + ')');
          lines.push(['Buy Date', 'Qty', 'Unit Cost', 'Cost Base', 'Proceeds', 'Gain', 'Days Held', 'Discount Eligible'].join(','));
          d.persisted_allocation.allocations.forEach(a => {
            lines.push([
              this.showDateFormat(a.buy_date),
              a.qty,
              a.unit_cost.toFixed(2),
              a.cost_base.toFixed(2),
              a.proceeds.toFixed(2),
              a.capital_gain.toFixed(2),
              a.holding_days,
              a.discount_eligible ? 'Yes' : 'No'
            ].join(','));
          });
          lines.push(['', '', '', '', 'Total', d.persisted_allocation.total_gain.toFixed(2), '', ''].join(','));
        }
      }

      // ticker transaction timelines
      if (this.report.ticker_timelines && this.report.ticker_timelines.length > 0) {
        lines.push('');
        lines.push('Transaction Timelines');

        for (const timeline of this.report.ticker_timelines) {
          const sells = this.timelineSells(timeline);
          const sellIds = new Set(sells.map(s => s.sell_txn_id));
          const buyAllocMap = {};
          for (const alloc of timeline.allocations) {
            if (!buyAllocMap[alloc.buy_txn_id]) buyAllocMap[alloc.buy_txn_id] = [];
            buyAllocMap[alloc.buy_txn_id].push(alloc);
          }

          lines.push('');
          lines.push(timeline.ticker + ' transaction history');
          lines.push(['Date', 'Type', 'Qty', 'Price', 'Net Amount', 'Allocated'].join(','));

          for (const txn of timeline.txns) {
            let allocText = '';
            if (txn.txn_type === 'sell' && sellIds.has(txn.id)) {
              allocText = 'disposal';
            } else if (buyAllocMap[txn.id]) {
              allocText = buyAllocMap[txn.id].map(a => a.qty + '/' + a.buy_qty + ' -> sell ' + this.showDateFormat(a.sell_date)).join('; ');
            }
            lines.push([
              this.showDateFormat(txn.txn_date),
              txn.txn_type,
              txn.qty,
              txn.price != null ? txn.price.toFixed(2) : '',
              txn.net_amount != null ? txn.net_amount.toFixed(2) : '',
              allocText
            ].join(','));
          }
        }
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cgt_disposals_' + this.startDate + '_' + this.endDate + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
* {
  font-family: "Space Grotesk", "Courier New", Courier, monospace, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

:deep(*) {
  font-family: "Space Grotesk", "Courier New", Courier, monospace, sans-serif !important;
}

table {
  width: 100%;
  border-collapse: collapse;
}

.detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85em;
}

.detail-table th, .detail-table td {
  padding: 4px 8px;
}

.timeline-sell-row {
  font-weight: bold;
  background-color: rgba(25, 118, 210, 0.06);
}

.timeline-allocated-row {
  background-color: rgba(25, 118, 210, 0.03);
}

.timeline-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 3px;
}

.timeline-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}

.timeline-badge {
  display: inline-block;
  color: #fff;
  font-size: 0.75em;
  font-weight: bold;
  padding: 1px 6px;
  border-radius: 8px;
  margin-right: 3px;
}

.step-result {
  border-top: 1px solid #ccc;
  margin-top: 2px;
  padding-top: 2px;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #000;
  color: #fff;
  font-size: 0.7em;
  font-weight: bold;
  margin-right: 8px;
  flex-shrink: 0;
}

.cf-save-btn :deep(.v-icon) {
  transition: opacity 0.3s ease, color 0.3s ease, transform 0.3s ease;
}

.cf-save-btn :deep(.v-icon.cf-icon-fade) {
  opacity: 0.85;
  transform: scale(1.1);
}
</style>
